import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { t } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';

// Bank verbinden im Ordner „Kontoauszug“ (server/src/modules/rechnungstool-bank/): Der Inhaber meldet sich einmal im
// Fenster von Ponto bei seiner Bank an. Nach jedem abgeschlossenen Monat legt der Server den Kontoauszug (CAMT) von
// selbst in diesen Ordner, die Kanzlei bucht ihn wie einen hochgeladenen.

// so viele Tage vor Ablauf erinnert die Karte ans Erneuern
const ERNEUERN_TAGE = 14;

interface BankStand {
  verfuegbar: boolean;
  test: boolean;
  inhaber: boolean;
  verbunden: boolean;
  getrennt: boolean;
  fehler: string | null;
  freigabeBis: string | null;
  konten: { iban: string; name: string | null; bank: string | null }[];
}

export function BankAuszug({ clientId, year, month }: { clientId: string; year: number; month: number }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const res = useApi<BankStand>(`/api/rechnungstool/${clientId}/bank`);
  const [laeuft, setLaeuft] = useState(false);
  const [trennen, setTrennen] = useState(false);
  const s = res.data;
  const ziel = `/app/belege/${clientId}/${year}/${month}/kontoauszug`;

  // zurück aus dem Fenster von Ponto
  const rueck = params.get('bank');
  useEffect(() => {
    if (!rueck) return;
    const text: Record<string, string> = {
      ok: t('Bank verbunden. Kontoauszüge kommen jetzt nach jedem Monat von selbst.'),
      erneuert: t('Freigabe erneuert.'),
      abgebrochen: t('Bank verbinden abgebrochen.'),
      fehler: t('Die Bank ließ sich gerade nicht verbinden. Bitte noch einmal versuchen.'),
    };
    if (text[rueck]) toast(text[rueck]);
    const p = new URLSearchParams(params);
    p.delete('bank');
    setParams(p, { replace: true });
    // nach dem Erneuern die neue Frist holen
    if (rueck === 'erneuert') void api.post(`/api/rechnungstool/${clientId}/bank/abrufen`, {}).catch(() => undefined).then(() => res.reload());
  }, [rueck]); // eslint-disable-line react-hooks/exhaustive-deps

  async function weiter(pfad: 'verbinden' | 'erneuern') {
    setLaeuft(true);
    try {
      const r = await api.post<{ url: string }>(`/api/rechnungstool/${clientId}/bank/${pfad}`, { ziel });
      window.location.assign(r.url);
    } catch (err) {
      toast(errorText(err));
      setLaeuft(false);
    }
  }

  async function trennenJetzt() {
    setLaeuft(true);
    try {
      await api.del(`/api/rechnungstool/${clientId}/bank`);
      toast(t('Bank getrennt'));
      setTrennen(false);
      await res.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setLaeuft(false);
    }
  }

  // Server ohne Ponto: keine Karte
  if (!s?.verfuegbar) return null;
  const test = s.test && <span className="bank-test">{t('Test')}</span>;

  if (!s.verbunden) {
    return (
      <div className="card bankauszug">
        <b>
          {t('Kontoauszug von selbst')} {test}
        </b>
        {s.getrennt ? (
          <p className="bank-fehler">{s.fehler ?? t('Die Verbindung zur Bank ist beendet. Bitte neu verbinden.')}</p>
        ) : (
          <p>{t('Verbinden Sie Ihre Bank, dann liegt hier nach jedem Monat der Kontoauszug, ohne Hochladen.')}</p>
        )}
        {s.inhaber ? (
          <>
            <div className="bwa-row">
              <button type="button" className="btn2 dark" disabled={laeuft} onClick={() => void weiter('verbinden')}>
                {laeuft ? t('Öffnet …') : s.getrennt ? t('Neu verbinden') : t('Bank verbinden')}
              </button>
            </div>
            <small>
              {t('Über Ponto, einen von der Bankenaufsicht zugelassenen Dienst. Wir sehen nur Ihre Umsätze, nie Ihre PIN oder TAN.')}
            </small>
          </>
        ) : (
          <small>{t('Das kann der Inhaber der Firma in der App einrichten.')}</small>
        )}
      </div>
    );
  }

  const tage = s.freigabeBis ? Math.ceil((new Date(s.freigabeBis).getTime() - Date.now()) / 86_400_000) : null;
  const bald = tage !== null && tage <= ERNEUERN_TAGE;
  return (
    <div className="card bankauszug an">
      <b>
        {t('Bank verbunden')} {test}
      </b>
      <ul>
        {s.konten.map((k) => (
          <li key={k.iban}>
            {k.bank ?? k.name ?? t('Konto')} <span>{k.iban}</span>
          </li>
        ))}
      </ul>
      <p>{t('Der Kontoauszug kommt nach Monatsende von selbst in diesen Ordner. Ihre Kanzlei sieht ihn dann.')}</p>
      {s.fehler && <p className="bank-fehler">{s.fehler}</p>}
      {bald ? (
        <p className="bank-fehler">
          {tage! > 0
            ? tage === 1
              ? t('Die Freigabe Ihrer Bank läuft morgen ab.')
              : t('Die Freigabe Ihrer Bank läuft in {n} Tagen ab.', { n: tage! })
            : t('Die Freigabe Ihrer Bank ist abgelaufen.')}
        </p>
      ) : (
        s.freigabeBis && <small>{t('Freigabe bis {datum}', { datum: fmtDate(s.freigabeBis.slice(0, 10)) })}</small>
      )}
      {s.inhaber &&
        (trennen ? (
          <div className="bank-trennen">
            <span>{t('Bank trennen? Gespeicherte Kontoauszüge bleiben im Ordner.')}</span>
            <div className="bwa-row">
              <button type="button" className="btn2 dark" disabled={laeuft} onClick={() => void trennenJetzt()}>
                {laeuft ? t('Trennt …') : t('Bank trennen')}
              </button>
              <button type="button" className="btn2" onClick={() => setTrennen(false)}>
                {t('Abbrechen')}
              </button>
            </div>
          </div>
        ) : (
          <div className="bwa-row">
            {bald && (
              <button type="button" className="btn2 dark" disabled={laeuft} onClick={() => void weiter('erneuern')}>
                {t('Freigabe erneuern')}
              </button>
            )}
            <button type="button" className="btn2" disabled={laeuft} onClick={() => setTrennen(true)}>
              {t('Trennen')}
            </button>
          </div>
        ))}
    </div>
  );
}
